/**
 * UsageIndicator Component
 *
 * Displays current AI generation usage with visual progress bar.
 * Shows warning state when approaching limit, and triggers upgrade modal when exceeded.
 */

import { useMemo, useState } from 'react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle2, Sparkles, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { UpgradeModal } from './UpgradeModal';

interface UsageIndicatorProps {
  currentUsage: number;
  limit: number | null; // null = unlimited (Pro)
  tier: 'free' | 'pro' | 'enterprise';
  status: 'trial' | 'active' | 'past_due' | 'canceled';
  trialEndsAt?: Date | null;
  compact?: boolean;
  showUpgradeButton?: boolean;
}

const FREE_TIER_LIMIT = 25;

export function UsageIndicator({
  currentUsage,
  limit,
  tier,
  status,
  trialEndsAt,
  compact = false,
  showUpgradeButton = true,
}: UsageIndicatorProps) {
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  const isPro = tier === 'pro' && status === 'active';
  const effectiveLimit = limit ?? FREE_TIER_LIMIT;

  const usagePercentage = useMemo(() => {
    if (isPro) return 0; // Pro has no limit, show empty bar
    return Math.min((currentUsage / effectiveLimit) * 100, 100);
  }, [currentUsage, effectiveLimit, isPro]);

  const isNearLimit = !isPro && usagePercentage >= 80;
  const isAtLimit = !isPro && currentUsage >= effectiveLimit;

  // Calculate days remaining in trial
  const trialDaysRemaining = useMemo(() => {
    if (status !== 'trial' || !trialEndsAt) return null;
    const now = new Date();
    const end = new Date(trialEndsAt);
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }, [status, trialEndsAt]);

  const getUpgradeReason = (): 'limit_reached' | 'trial_expired' | 'upgrade' => {
    if (isAtLimit) return 'limit_reached';
    if (trialDaysRemaining === 0) return 'trial_expired';
    return 'upgrade';
  };

  if (compact) {
    return (
      <>
        <div className="flex items-center gap-2">
          {isPro ? (
            <Badge variant="secondary" className="bg-primary/10 text-primary">
              <Sparkles className="mr-1 h-3 w-3" />
              Pro
            </Badge>
          ) : (
            <>
              <span className="text-sm text-muted-foreground">
                {currentUsage}/{effectiveLimit}
              </span>
              <Progress
                value={usagePercentage}
                className={cn(
                  'h-2 w-20',
                  isAtLimit && 'bg-destructive/20 [&>div]:bg-destructive',
                  isNearLimit && !isAtLimit && 'bg-amber-100 [&>div]:bg-amber-500'
                )}
              />
              {showUpgradeButton && !isPro && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setUpgradeModalOpen(true)}
                >
                  <Zap className="mr-1 h-3 w-3" />
                  Upgrade
                </Button>
              )}
            </>
          )}
        </div>

        <UpgradeModal
          open={upgradeModalOpen}
          onOpenChange={setUpgradeModalOpen}
          reason={getUpgradeReason()}
          currentUsage={currentUsage}
          usageLimit={effectiveLimit}
        />
      </>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">AI Generations</span>
            {isPro ? (
              <Badge variant="secondary" className="bg-primary/10 text-primary">
                <Sparkles className="mr-1 h-3 w-3" />
                Unlimited
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className={cn(
                  isAtLimit && 'border-destructive text-destructive',
                  isNearLimit && !isAtLimit && 'border-amber-500 text-amber-600'
                )}
              >
                {currentUsage} / {effectiveLimit}
              </Badge>
            )}
          </div>

          {/* Trial warning */}
          {status === 'trial' && trialDaysRemaining !== null && trialDaysRemaining <= 3 && (
            <Badge
              variant="outline"
              className={cn(
                'border-amber-500 text-amber-600',
                trialDaysRemaining === 0 && 'border-destructive text-destructive'
              )}
            >
              <AlertTriangle className="mr-1 h-3 w-3" />
              {trialDaysRemaining === 0
                ? 'Trial expired'
                : `${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} left`}
            </Badge>
          )}
        </div>

        {/* Progress bar (hidden for Pro) */}
        {!isPro && (
          <div className="space-y-1">
            <Progress
              value={usagePercentage}
              className={cn(
                'h-2',
                isAtLimit && 'bg-destructive/20 [&>div]:bg-destructive',
                isNearLimit && !isAtLimit && 'bg-amber-100 [&>div]:bg-amber-500'
              )}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{effectiveLimit - currentUsage} remaining</span>
              <span>Resets monthly</span>
            </div>
          </div>
        )}

        {/* Status messages */}
        {isAtLimit && (
          <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4" />
            <span>
              Generation limit reached.{' '}
              <button
                onClick={() => setUpgradeModalOpen(true)}
                className="font-medium underline underline-offset-2 hover:no-underline"
              >
                Upgrade to Pro
              </button>{' '}
              for unlimited access.
            </span>
          </div>
        )}

        {isNearLimit && !isAtLimit && (
          <div className="flex items-center gap-2 rounded-md bg-amber-50 p-2 text-sm text-amber-700 dark:bg-amber-950/30 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4" />
            <span>
              Approaching limit ({effectiveLimit - currentUsage} left).{' '}
              <button
                onClick={() => setUpgradeModalOpen(true)}
                className="font-medium underline underline-offset-2 hover:no-underline"
              >
                Upgrade now
              </button>
            </span>
          </div>
        )}

        {isPro && (
          <div className="flex items-center gap-2 rounded-md bg-primary/10 p-2 text-sm text-primary">
            <CheckCircle2 className="h-4 w-4" />
            <span>Pro plan active - unlimited AI generations</span>
          </div>
        )}

        {/* Upgrade button */}
        {showUpgradeButton && !isPro && !isAtLimit && !isNearLimit && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setUpgradeModalOpen(true)}
            className="w-full"
          >
            <Zap className="mr-2 h-4 w-4" />
            Upgrade to Pro
          </Button>
        )}
      </div>

      <UpgradeModal
        open={upgradeModalOpen}
        onOpenChange={setUpgradeModalOpen}
        reason={getUpgradeReason()}
        currentUsage={currentUsage}
        usageLimit={effectiveLimit}
      />
    </>
  );
}

export default UsageIndicator;
